import { useCallback, useMemo } from "react";
import { MenuItemOptionElement, AddItemOptionElementInput } from "src/interfaces/menu.interface";
import { UseMenuItemOptionModalLogicProps } from "src/interfaces/menu-items-props.interface";
import { UUID } from "src/types/shared.type";

export const useMenuItemOptionModalLogic = ({
  formik,
  menuItemOption,
  menuItemOptionIndex,
  menuItemOptionElementsToRender,
  isUpdatingOption,
  getMenuItems,
}: UseMenuItemOptionModalLogicProps) => {

	const {values, setFieldValue} = formik

  // Use Formik validation instead of custom logic
  const optionFieldPath = `itemOptionInputList[${menuItemOptionIndex}]`;
  const optionNameError = formik.getFieldMeta(`${optionFieldPath}.name`).error;
  const optionSequenceError = formik.getFieldMeta(`${optionFieldPath}.renderSequence`).error;
  
  const isUpdateButtonDisabled = useMemo(() => {
    return !menuItemOption?.name ||
      menuItemOption?.renderSequence == null ||
      isUpdatingOption ||
      !!(optionNameError || optionSequenceError);
  }, [menuItemOption?.name, menuItemOption?.renderSequence, isUpdatingOption, optionNameError, optionSequenceError]);

  const isDoneButtonDisabled = useMemo(() => {
    if (!menuItemOption?.name || menuItemOption?.renderSequence == null || optionNameError || optionSequenceError) {
      return true;
    }

    return menuItemOptionElementsToRender.some(
      (menuItemOptionElement: MenuItemOptionElement, elementIndex: number) => {
        const elementFieldPath = `${optionFieldPath}.addItemOptionElementInputList[${elementIndex}]`;
        const elementSequenceError = formik.getFieldMeta(`${elementFieldPath}.renderSequence`).error;
        const elementPriceError = formik.getFieldMeta(`${elementFieldPath}.priceCents`).error;
        
        const hasMenuItemOptionElementValue = !!(
          menuItemOptionElement?.name?.trim() ||
          menuItemOptionElement?.renderSequence != null ||
          menuItemOptionElement?.priceCents != null
        );
        
        const hasErrors = !!( elementSequenceError || elementPriceError);
        return hasMenuItemOptionElementValue && hasErrors;
      }
    );
  }, [menuItemOption?.name, menuItemOption?.renderSequence, menuItemOptionElementsToRender, optionFieldPath, optionNameError, optionSequenceError, formik]);

  // Mutation success handlers
  const handleSaveNewMenuItemOptionSuccess = useCallback(({ correlationId, menuItemOptionElements }: { correlationId: UUID; menuItemOptionElements: MenuItemOptionElement[] }) => {
    const menuItemOptions = [...values.itemOptionInputList];
    const menuItemOption = { ...menuItemOptions[menuItemOptionIndex] };
    menuItemOption.correlationId = correlationId;
    
    if (Array.isArray(menuItemOption.addItemOptionElementInputList)) {
      menuItemOption.addItemOptionElementInputList =
        (menuItemOption.addItemOptionElementInputList as AddItemOptionElementInput[]).map(
          (
            menuItemOptionElement: AddItemOptionElementInput,
            menuItemOptionElementIndex: number
          ) => ({
            description: menuItemOptionElement.description ?? "",
            itemOptionCorrelationId: menuItemOptionElement.itemOptionCorrelationId,
            name: menuItemOptionElement.name ?? "",
            priceCents: menuItemOptionElement.priceCents ?? 0,
            renderSequence: menuItemOptionElement.renderSequence ?? 0,
            correlationId:
              menuItemOptionElements?.[menuItemOptionElementIndex]?.correlationId ??
              menuItemOptionElement?.correlationId,
          })
        );
    }
    
    menuItemOptions[menuItemOptionIndex] = menuItemOption;
    setFieldValue("itemOptionInputList", menuItemOptions);
    getMenuItems();
  }, [menuItemOptionIndex, getMenuItems, values.itemOptionInputList, setFieldValue]);

  const handleSaveNewOptionElementSuccess = useCallback((menuItemOptionElementCorrelationId: UUID, menuItemOptionElementIndex: number) => {
    const menuItemOptions = [...values.itemOptionInputList];
    const menuItemOption = {
      ...menuItemOptions[menuItemOptionIndex],
    };
    const menuItemOptionElements = [
      ...(menuItemOption.addItemOptionElementInputList || []),
    ];
    
    if (menuItemOptionElements[menuItemOptionElementIndex]) {
      menuItemOptionElements[menuItemOptionElementIndex] = {
        ...menuItemOptionElements[menuItemOptionElementIndex],
        correlationId: menuItemOptionElementCorrelationId,
      };
    }
    
    menuItemOption.addItemOptionElementInputList = menuItemOptionElements;
    menuItemOptions[menuItemOptionIndex] = menuItemOption;
    setFieldValue("itemOptionInputList", menuItemOptions);
    getMenuItems();
  }, [menuItemOptionIndex, getMenuItems, values.itemOptionInputList, setFieldValue]);

  const handleUpdateMenuItemOptionSuccess = useCallback(() => {
    getMenuItems();
  }, [getMenuItems]);

  const handleUpdateMenuItemOptionElementSuccess = useCallback((isDeleted: boolean, menuItemOptionElementIndex: number, deleteOptionElementFromForm: (index: number) => void) => {
    if (isDeleted) {
      deleteOptionElementFromForm(menuItemOptionElementIndex);
    }
  }, []);

  const handleCloseModal = useCallback((onClose: () => void) => {
    if (
      menuItemOptionIndex ===
      values.itemOptionInputList.length - 1
    ) {
      const lastMenuItemOption =
        values.itemOptionInputList[menuItemOptionIndex];

      if (
        lastMenuItemOption &&
        (!lastMenuItemOption.name?.trim() ||
          lastMenuItemOption.renderSequence == null)
      ) {
        const filteredMenuItemOptions =
          values.itemOptionInputList.slice(0, -1);
        setFieldValue("itemOptionInputList", filteredMenuItemOptions);
      }
    }

    onClose();
  }, [menuItemOptionIndex, values.itemOptionInputList, setFieldValue]);

  return {
    // Validation
    isUpdateButtonDisabled,
    isDoneButtonDisabled,
    // Success handlers
    handleSaveNewMenuItemOptionSuccess,
    handleSaveNewOptionElementSuccess,
    handleUpdateMenuItemOptionSuccess,
    handleUpdateMenuItemOptionElementSuccess,
    handleCloseModal,
  };
};
